import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlertIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';

/**
 * Vangt een fout in één scherm op, zodat de rest van de app blijft staan.
 *
 * Zonder dit haalt React bij een renderfout de hele boom weg en zie je een wit
 * venster, op élke pagina, want de navigatie verdwijnt mee. Dat is precies wat
 * er gebeurde toen het beheerscherm een object probeerde te tonen waar een
 * string werd verwacht: één verkeerde aanname in één sectie, en de applicatie
 * was onbruikbaar.
 *
 * Een foutgrens is geen vervanging voor de fout oplossen. Hij zorgt dat een
 * kapot scherm een kapot scherm blijft in plaats van een kapotte applicatie.
 */
interface Props {
  children: ReactNode;
  /** Verandert deze waarde, dan proberen we opnieuw, bijvoorbeeld bij navigatie. */
  sleutel?: string;
}

interface State {
  fout: Error | null;
  sleutel?: string;
}

export class Foutgrens extends Component<Props, State> {
  override state: State = { fout: null };

  static getDerivedStateFromError(fout: Error): Partial<State> {
    return { fout };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // Naar een ander scherm navigeren wist de fout: die hoorde bij het vorige.
    if (props.sleutel !== state.sleutel) return { fout: null, sleutel: props.sleutel };
    return null;
  }

  override componentDidCatch(fout: Error, info: ErrorInfo) {
    console.error('[VIP] scherm liep vast:', fout, info.componentStack);
  }

  override render() {
    if (!this.state.fout) return this.props.children;
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <TriangleAlertIcon />
          </EmptyMedia>
          <EmptyTitle>Dit scherm liep vast.</EmptyTitle>
          {/* De melding erbij, want dit is wat je doorgeeft als je hulp vraagt. */}
          <EmptyDescription>{this.state.fout.message}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button onClick={() => this.setState({ fout: null })} variant="outline">
            Opnieuw proberen
          </Button>
        </EmptyContent>
      </Empty>
    );
  }
}
